/**
 * useCamera — BarcodeScannerModal
 *
 * Self-contained barcode/QR scanning modal built on useCamera for camera
 * management (stream, torch, permissions) and BarcodeDetector API (preferred)
 * with ZXing fallback. Provides auto-torch and auto-zoom support that the
 * standalone BarcodeScanner.ts Capacitor plugin lacks.
 *
 * Designed to replace:
 *   - LicenseDiskScanner's custom camera UI
 *   - BarcodeScanner.ts (Capacitor plugin with web fallback)
 *
 * Usage:
 * ```tsx
 * <BarcodeScannerModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onScan={(result) => console.log(result.content, result.format)}
 *   formats={['pdf417', 'qr_code', 'data_matrix']}
 *   title="Scan License Disk"
 *   prompt="Position the barcode within the frame"
 *   autoTorch
 * />
 * ```
 */

'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCamera } from './useCamera';
import { useOrientation } from './useOrientation';
import { CameraActionButton } from './CameraActionButton';
import { IconClose, TorchIcon, IconCamera } from './icons';
import { haptics } from '@/lib/haptics';
import type { BarcodeFormat } from './barcodeTypes';

// ── Types ────────────────────────────────────────────────────────────────────

export interface BarcodeScanResult {
  /** Scanned content (raw text value) */
  content: string;
  /** Barcode format detected */
  format: BarcodeFormat;
  /** Raw bytes as hex string (if available) */
  rawBytes?: string;
}

export interface BarcodeScannerModalProps {
  /** Whether the scanner modal is open */
  isOpen: boolean;
  /** Called when the user closes the modal without scanning */
  onClose: () => void;
  /** Called when a barcode is successfully scanned */
  onScan: (result: BarcodeScanResult) => void;
  /** Barcode formats to scan for (default: all supported) */
  formats?: BarcodeFormat[];
  /** Title shown in header (default: 'Scan Barcode') */
  title?: string;
  /** Prompt text shown below the scanning frame (default: 'Position barcode within the frame') */
  prompt?: string;
  /** Enable auto-torch in low light (default: true) */
  autoTorch?: boolean;
  /** Enable auto-zoom to fill frame with barcode (default: false) */
  autoZoom?: boolean;
  /** Show close button (default: true) */
  showCloseButton?: boolean;
  /** Initial camera facing mode (default: 'environment') */
  initialFacingMode?: 'user' | 'environment';
  /** JPEG quality for the camera (default: 0.85) */
  quality?: number;
}

// ── BarcodeDetector type (not yet in all TS defs) ────────────────────────────

interface DetectedBarcode {
  rawValue: string;
  format: string;
  boundingBox: DOMRectReadOnly;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

type BarcodeDetectorConstructor = new (options?: {
  formats?: string[];
}) => BarcodeDetectorLike;

type WindowWithBarcodeDetector = Window & {
  BarcodeDetector?: BarcodeDetectorConstructor;
};

// ── BarcodeDetector format name mapping ────────────────────────────────────
// The BarcodeDetector API uses lowercase format names. Most keep their
// underscores (e.g. 'qr_code', 'data_matrix'), but 'pdf417' is the exception
// — it has NO underscore. The BarcodeFormat type uses uppercase with
// underscores (matching the Capacitor BarcodeScanner plugin).
const BARCODE_DETECTOR_FORMAT_MAP: Record<string, string> = {
  QR_CODE: 'qr_code',
  AZTEC: 'aztec',
  CODABAR: 'codabar',
  CODE_39: 'code_39',
  CODE_93: 'code_93',
  CODE_128: 'code_128',
  DATA_MATRIX: 'data_matrix',
  EAN_8: 'ean_8',
  EAN_13: 'ean_13',
  ITF: 'itf',
  PDF_417: 'pdf417',
  UPC_A: 'upc_a',
  UPC_E: 'upc_e',
};

// ── Component ────────────────────────────────────────────────────────────────

export function BarcodeScannerModal({
  isOpen,
  onClose,
  onScan,
  formats,
  title = 'Scan Barcode',
  prompt = 'Position barcode within the frame',
  autoTorch = true,
  autoZoom = false,
  showCloseButton = true,
  initialFacingMode = 'environment',
  quality = 0.85,
}: BarcodeScannerModalProps) {
  const isLandscape = useOrientation();
  const [error, setError] = useState<string | null>(null);
  const [isDecoding, setIsDecoding] = useState(false);
  const [torchMode, setTorchMode] = useState<'off' | 'on'>('off');

  const cancelledRef = useRef(false);
  const detectRafRef = useRef<number | null>(null);
  const detectorRef = useRef<BarcodeDetectorLike | null>(null);
  const codeReaderRef = useRef<{ stopAsyncDecode: () => void } | null>(null);
  const scannedRef = useRef(false);

  // Camera hook — manages stream, torch, permissions
  const camera = useCamera({
    initialFacingMode,
    enableTorchByDefault: autoTorch,
    preferNativeCameraOnCapacitor: false,
    quality,
    onCapture: () => {
      // Not used for scanning — BarcodeDetector/ZXing handles decode
    },
    onClose: () => {
      // Cleanup detection when camera closes
      cancelledRef.current = true;
      if (detectRafRef.current) {
        cancelAnimationFrame(detectRafRef.current);
        detectRafRef.current = null;
      }
      if (codeReaderRef.current) {
        try { codeReaderRef.current.stopAsyncDecode(); } catch { /* ignore */ }
        codeReaderRef.current = null;
      }
      detectorRef.current = null;
    },
  });

  const { videoRef, stream, isLoading, torchEnabled, torchSupported, toggleTorch, open, close } = camera;

  // Open camera when modal opens
  useEffect(() => {
    if (isOpen && !camera.isOpen) {
      scannedRef.current = false;
      setError(null);
      open();
    }
    if (!isOpen && camera.isOpen) {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close camera on unmount
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (detectRafRef.current) {
        cancelAnimationFrame(detectRafRef.current);
      }
      if (codeReaderRef.current) {
        try { codeReaderRef.current.stopAsyncDecode(); } catch { /* ignore */ }
      }
    };
  }, []);

  // Handle scan result — deduplicates and fires onScan once
  const handleResult = useCallback((content: string, format: BarcodeFormat) => {
    if (scannedRef.current || !content) return;
    scannedRef.current = true;
    void haptics.success();
    onScan({ content, format });
    // Close after a brief delay so the user sees the success feedback
    setTimeout(() => close(), 150);
  }, [onScan, close]);

  // Start barcode detection when stream is available
  useEffect(() => {
    if (!stream || !videoRef.current || scannedRef.current) return;

    let cancelled = false;
    cancelledRef.current = false;

    (async () => {
      const video = videoRef.current;
      if (!video || cancelled) return;

      // ── Method 1: BarcodeDetector API (preferred — native, fast) ──
      const win = typeof window !== 'undefined' ? window as WindowWithBarcodeDetector : undefined;
      if (win?.BarcodeDetector) {
        try {
          // Map uppercase BarcodeFormat names to BarcodeDetector API format
          // strings (lowercase, with underscores except pdf417).
          const formatStrings = formats?.map(f => BARCODE_DETECTOR_FORMAT_MAP[f] ?? f.toLowerCase()) || undefined;
          const detector = new win.BarcodeDetector({ formats: formatStrings });
          detectorRef.current = detector;
          setIsDecoding(true);

          const detectLoop = async () => {
            if (cancelled || scannedRef.current || !videoRef.current) return;
            try {
              const barcodes = await detector.detect(videoRef.current);
              // Check cancelled after the async detect() — the effect may
              // have been cleaned up while waiting.
              if (cancelled || scannedRef.current) return;
              if (barcodes.length > 0 && barcodes[0].rawValue) {
                const detectedFormat = barcodes[0].format.toUpperCase().replace(/-/g, '_') as BarcodeFormat;
                handleResult(barcodes[0].rawValue, detectedFormat);
                return;
              }
            } catch {
              // detect() throws when video isn't ready — ignore
            }
            if (!cancelled && !scannedRef.current) {
              detectRafRef.current = requestAnimationFrame(detectLoop);
            }
          };
          detectLoop();
          return;
        } catch (err) {
          console.warn('[BarcodeScannerModal] BarcodeDetector init failed, falling back to ZXing:', err);
        }
      }

      // ── Method 2: ZXing fallback (for iOS Safari etc.) ──
      try {
        const { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat: ZxingFormat } = await import('@zxing/library');
        if (cancelled || scannedRef.current || !videoRef.current) return;

        const hints = new Map();
        const zxingFormats = (formats || ['pdf417', 'qr_code', 'data_matrix']).map(f => {
          const mapping: Record<string, number> = {
            pdf_417: ZxingFormat.PDF_417,
            qr_code: ZxingFormat.QR_CODE,
            data_matrix: ZxingFormat.DATA_MATRIX,
            code_128: ZxingFormat.CODE_128,
            code_39: ZxingFormat.CODE_39,
            ean_13: ZxingFormat.EAN_13,
            ean_8: ZxingFormat.EAN_8,
            itf: ZxingFormat.ITF,
            codabar: ZxingFormat.CODABAR,
            aztec: ZxingFormat.AZTEC,
            upc_a: ZxingFormat.UPC_A,
            upc_e: ZxingFormat.UPC_E,
            code_93: ZxingFormat.CODE_93,
          };
          return mapping[f.toLowerCase()] ?? ZxingFormat.PDF_417;
        });
        hints.set(DecodeHintType.POSSIBLE_FORMATS, zxingFormats);
        hints.set(DecodeHintType.TRY_HARDER, true);

        const codeReader = new BrowserMultiFormatReader(hints);
        codeReaderRef.current = codeReader;
        setIsDecoding(true);

        codeReader.decodeFromVideoElementContinuously(videoRef.current, (result) => {
          if (result && result.getText() && !scannedRef.current) {
            const detectedFormat = (result.getBarcodeFormat?.() ?? ZxingFormat.PDF_417).toString().toUpperCase().replace(/-/g, '_') as BarcodeFormat;
            handleResult(result.getText(), detectedFormat);
          }
        });
      } catch (err) {
        console.error('[BarcodeScannerModal] Failed to start any decoder:', err);
        setError('Barcode scanning not available on this device');
        setIsDecoding(false);
      }
    })();

    return () => {
      cancelled = true;
      cancelledRef.current = true;
      if (detectRafRef.current) {
        cancelAnimationFrame(detectRafRef.current);
        detectRafRef.current = null;
      }
      if (codeReaderRef.current) {
        try { codeReaderRef.current.stopAsyncDecode(); } catch { /* ignore */ }
        codeReaderRef.current = null;
      }
      detectorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  // Auto-torch: turn on when ambient brightness is low, turn off when it
  // recovers. Uses hysteresis (on < 100, off > 130) to prevent oscillation
  // when a white barcode reflects torch light back at the sensor.
  const { ambientBrightness } = camera;
  useEffect(() => {
    if (!autoTorch || !isOpen || !stream || !torchSupported) return;
    if (ambientBrightness === null) return;

    // Turn on when dark and torch is off
    if (ambientBrightness < 100 && !torchEnabled) {
      void toggleTorch();
      setTorchMode('on');
    }
    // Turn off when bright again and torch is on (hysteresis: > 130)
    if (ambientBrightness > 130 && torchEnabled) {
      void toggleTorch();
      setTorchMode('off');
    }
  }, [autoTorch, isOpen, stream, ambientBrightness, torchEnabled, torchSupported, toggleTorch]);

  // Handle torch toggle
  const handleTorchToggle = useCallback(async () => {
    await toggleTorch();
    setTorchMode(prev => prev === 'off' ? 'on' : 'off');
  }, [toggleTorch]);

  // Handle close
  const handleClose = useCallback(() => {
    void haptics.modalClose();
    close();
    onClose();
  }, [close, onClose]);

  if (!isOpen) return null;

  const modalZIndexClassName = 'z-[300]';

  // ── Scanning frame ──
  const scanFrame = (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
      <div
        className="relative rounded-2xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"
        style={{
          width: isLandscape ? '60%' : '70%',
          height: isLandscape ? '60%' : '45%',
        }}
      >
        {/* Corner markers */}
        {[
          { className: 'top-0 left-0 border-t-[3px] border-l-[3px] rounded-tl-lg' },
          { className: 'top-0 right-0 border-t-[3px] border-r-[3px] rounded-tr-lg' },
          { className: 'bottom-0 left-0 border-b-[3px] border-l-[3px] rounded-bl-lg' },
          { className: 'bottom-0 right-0 border-b-[3px] border-r-[3px] rounded-br-lg' },
        ].map((corner, i) => (
          <div key={i} className={`absolute w-8 h-8 ${corner.className} border-white`} />
        ))}
        {/* Scanning line animation */}
        <div className="absolute left-2 right-2 top-0 h-0.5 bg-green-400/80 shadow-[0_0_8px_rgba(74,222,128,0.6)]"
          style={{ animation: 'barcodeScan 2s ease-in-out infinite' }} />
      </div>
    </div>
  );

  const promptOverlay = prompt && (
    <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20">
      <div className="rounded-full bg-black/60 backdrop-blur-sm px-4 py-2">
        <span className="text-white text-sm font-medium">{prompt}</span>
      </div>
    </div>
  );

  const errorOverlay = error && (
    <div className="absolute inset-0 bg-black flex items-center justify-center z-30">
      <div className="text-center px-6">
        <IconCamera />
        <p className="text-white text-lg mb-2">Scanner Error</p>
        <p className="text-gray-400 text-sm mb-4">{error}</p>
        <button
          onClick={() => { setError(null); open(); }}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg"
        >
          Retry
        </button>
      </div>
    </div>
  );

  const loadingOverlay = isLoading && (
    <div className="absolute inset-0 bg-black flex flex-col items-center justify-center z-10">
      <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin mb-4" />
      <p className="text-white text-sm font-semibold">Starting camera...</p>
    </div>
  );

  const modalContent = (
    <div
      className={`fixed inset-0 bg-black ${modalZIndexClassName}`}
      style={{ display: 'flex', flexDirection: 'column', height: '100dvh' }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-2 bg-black/80"
        style={{ flexShrink: 0, paddingTop: 'max(10px, calc(env(safe-area-inset-top) + 4px))' }}
      >
        <span className="text-white font-medium text-sm flex-1 min-w-0 truncate">{title}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {torchSupported && (
            <button
              onClick={handleTorchToggle}
              className={`p-2 rounded-full transition-colors ${torchMode === 'on' ? 'bg-yellow-500/30 text-yellow-300' : 'text-white hover:bg-white/20'}`}
              aria-label={`Flash: ${torchMode}`}
            >
              <TorchIcon mode={torchMode} />
            </button>
          )}
          {showCloseButton && (
            <button
              onClick={handleClose}
              className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close scanner"
            >
              <IconClose />
            </button>
          )}
        </div>
      </div>

      {/* Video feed */}
      <div
        className="relative bg-black overflow-hidden"
        style={{ flex: '1 1 0%', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <video
          ref={camera.attachVideoElement}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />
        {loadingOverlay}
        {errorOverlay}
        {scanFrame}
        {promptOverlay}
      </div>

      {/* Scanning line keyframe */}
      <style>{`
        @keyframes barcodeScan {
          0% { top: 0; opacity: 1; }
          50% { top: calc(100% - 2px); opacity: 0.5; }
          100% { top: 0; opacity: 1; }
        }
      `}</style>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : null;
}

export default BarcodeScannerModal;
